import type { HeadCelType } from '~/utils/types/common';

/** Cột số thứ tự do API trả về (BC04) — thu hẹp cột này, không áp vào cột Ngày của fallback. */
export function isJourneySttColumnLabel(label: string | undefined): boolean {
  if (!label) return false;
  const s = label.trim().toLowerCase();
  return (
    s === 'stt' || s === 'no.' || s === 'no' || s === '#' || s === 'số tt' || /^số\s*thứ\s*tự$/i.test(label.trim())
  );
}

export function bumpTailwindMinWidthPx(className: string, delta: number): string {
  if (delta === 0) return className;
  return className.replace(/min-w-\[(\d+)px\]/g, (_, n) => `min-w-[${Number(n) + delta}px]`);
}

const JOURNEY_STT_HEAD_CLASS =
  'min-w-[64px] w-[64px] max-w-[72px] text-center border-l border-[#EBF1F8] border-b-0 px-2 box-border';

function normalizeReportHeaderLabel(label: string | undefined): string {
  return (label ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'd')
    .replace(/\s+/g, ' ');
}

/** Khớp nhãn cột "Trạng thái" / "Trạng thái giữa các lộ trình" / "Route status" / "Status". */
function isJourneyStatusColumnLabel(label: string | undefined): boolean {
  const s = normalizeReportHeaderLabel(label);
  if (!s) return false;
  return s.includes('trang thai') || s === 'status' || s.includes('route status');
}

function isJourneyStartOrEndTimeColumnLabel(label: string | undefined): boolean {
  const s = normalizeReportHeaderLabel(label);
  if (!s) return false;
  // Khớp lỏng cho mọi biến thể nhãn cột thời gian bắt đầu / kết thúc lộ trình
  // (vd. "Thời gian bắt đầu", "Thời gian bắt đầu lộ trình", "Bắt đầu lộ trình",
  // "Start time", "Route start time", "Trip start time", v.v.).
  const isStart =
    (s.includes('thoi gian') && s.includes('bat dau')) ||
    (s.includes('bat dau') && (s.includes('lo trinh') || s.includes('hanh trinh'))) ||
    s === 'start time' ||
    s.includes('start time') ||
    s.includes('start of trip') ||
    s.includes('trip start');
  const isEnd =
    (s.includes('thoi gian') && s.includes('ket thuc')) ||
    (s.includes('ket thuc') && (s.includes('lo trinh') || s.includes('hanh trinh'))) ||
    s === 'end time' ||
    s.includes('end time') ||
    s.includes('end of trip') ||
    s.includes('trip end');
  return isStart || isEnd;
}

/** Thu cột STT (nếu có) và tăng nhẹ min-width các cột còn lại — giữ tổng bảng ổn định. */
export function withJourneySttColumnLayout(headCells: HeadCelType[]): HeadCelType[] {
  if (headCells.length === 0 || !isJourneySttColumnLabel(String(headCells[0]?.label))) {
    return headCells.map((hc) =>
      isJourneyStartOrEndTimeColumnLabel(hc.label) ? { ...hc, sortable: false, searchable: false } : hc
    );
  }
  return headCells.map((hc, i) => {
    if (i === 0) {
      return {
        ...hc,
        sortable: false,
        searchable: false,
        className: JOURNEY_STT_HEAD_CLASS,
      };
    }
    return {
      ...hc,
      sortable: isJourneyStartOrEndTimeColumnLabel(hc.label) ? false : hc.sortable,
      searchable: isJourneyStartOrEndTimeColumnLabel(hc.label) ? false : hc.searchable,
      className: bumpTailwindMinWidthPx(hc.className ?? '', 12),
    };
  });
}

export const JOURNEY_SEGMENT_STATUS_BY_VALUE: Readonly<
  Record<number, Readonly<{ label: string; label_en: string; color: string }>>
> = {
  0: { label: 'Di chuyển', label_en: 'Moving', color: '#22C55E' },
  1: { label: 'Dừng', label_en: 'Stopped', color: '#9CA3AF' },
  2: { label: 'Mất tín hiệu', label_en: 'No Signal', color: '#F97316' },
  3: { label: 'Mất GPS', label_en: 'No GPS', color: '#EAB308' },
  4: { label: 'Vi phạm hàng rào', label_en: 'Geofence Violation', color: '#EF4444' },
  5: { label: 'Gỡ thiết bị trái phép', label_en: 'Device Tampered', color: '#7C3AED' },
};

const LEGACY_JOURNEY_STATUS_ENUM_TO_VALUE: Readonly<Record<string, number>> = {
  MOVING: 0,
  STOPPED: 1,
  SIGNAL_LOST: 2,
  GPS_LOST: 3,
  FENCE_VIOLATION: 4,
  UNAUTHORIZED_REMOVAL: 5,
};

const JOURNEY_ROW_FALLBACK_COLOR = '#161A1F';

function pickJourneyStatusLabel(entry: { label: string; label_en: string }, language: string): string {
  return language.toLowerCase().startsWith('en') ? entry.label_en : entry.label;
}

export function resolveJourneySegmentStatusCell(
  raw: string,
  language: string
): { displayText: string; rowColor: string } {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { displayText: '', rowColor: JOURNEY_ROW_FALLBACK_COLOR };
  }

  const asNum = Number.parseInt(trimmed, 10);
  if (!Number.isNaN(asNum) && asNum >= 0 && asNum <= 5) {
    const meta = JOURNEY_SEGMENT_STATUS_BY_VALUE[asNum];
    return {
      displayText: pickJourneyStatusLabel(meta, language),
      rowColor: meta.color,
    };
  }

  const legacy = LEGACY_JOURNEY_STATUS_ENUM_TO_VALUE[trimmed];
  if (legacy != null) {
    const meta = JOURNEY_SEGMENT_STATUS_BY_VALUE[legacy];
    return {
      displayText: pickJourneyStatusLabel(meta, language),
      rowColor: meta.color,
    };
  }

  const lower = trimmed.toLowerCase();
  for (let v = 0; v <= 5; v += 1) {
    const meta = JOURNEY_SEGMENT_STATUS_BY_VALUE[v];
    if (meta.label.toLowerCase() === lower || meta.label_en.toLowerCase() === lower) {
      return {
        displayText: pickJourneyStatusLabel(meta, language),
        rowColor: meta.color,
      };
    }
  }

  return { displayText: raw, rowColor: JOURNEY_ROW_FALLBACK_COLOR };
}

/**
 * Chuyển các ô ở cột "Trạng thái" (0/1/2/...) sang nhãn chữ tương ứng theo ngôn ngữ hiện tại
 * (vd. 0 → "Di chuyển" / "Moving") để khớp với hiển thị trên UI khi xuất Excel.
 * Các giá trị không phải mã trạng thái sẽ được giữ nguyên.
 */
export function mapJourneyStatusColumnInRows(
  headers: string[],
  data: string[][],
  language: string
): string[][] {
  if (headers.length === 0 || data.length === 0) return data;
  const statusIndices: number[] = [];
  for (let i = 0; i < headers.length; i += 1) {
    if (isJourneyStatusColumnLabel(headers[i])) {
      statusIndices.push(i);
    }
  }
  if (statusIndices.length === 0) return data;

  return data.map((row) => {
    let next: string[] | null = null;
    for (const idx of statusIndices) {
      const cell = row[idx];
      if (cell == null || cell === '') continue;
      const { displayText } = resolveJourneySegmentStatusCell(cell, language);
      if (displayText === cell) continue;
      if (next === null) next = [...row];
      next[idx] = displayText;
    }
    return next ?? row;
  });
}

export type ViolationFilterTypeKey = 'all' | 'deviceRemoval' | 'fenceViolation' | 'gathering';

/** Giá trị query `type` gửi lên `/api/data/bc02`: 0 tổng hợp, 1 tháo vòng, 2 vượt rào, 3 tập trung. */
export function bc02ViolationTypeParam(key: ViolationFilterTypeKey): string {
  switch (key) {
    case 'deviceRemoval':
      return '1';
    case 'fenceViolation':
      return '2';
    case 'gathering':
      return '3';
    case 'all':
    default:
      return '0';
  }
}

/** BC01 API table row: dynamic keys `col_0`, `col_1`, … plus stable `id` for React keys. */
export type Bc01TableRow = { id: string } & Record<string, string>;

const BC01_HEAD_CELL_CLASS = 'min-w-[128px] text-center border-l border-[#EBF1F8] border-b-0';

const HEADER_NO_SEARCH_LABELS = new Set(['Ngày', 'Đối tượng', 'Date', 'Subject', 'Tên đối tượng', 'Subject name']);

export function transformBc01HeadCells(headers: string[]): HeadCelType[] {
  return headers.map((label, index) => ({
    id: `col_${index}`,
    label,
    sortable: true,
    searchable: !HEADER_NO_SEARCH_LABELS.has(label),
    className: BC01_HEAD_CELL_CLASS,
  }));
}

export function transformBc01Rows(headers: string[], dataRows: string[][]): Bc01TableRow[] {
  return dataRows.map((cells, rowIndex) => {
    const row: Bc01TableRow = { id: `bc01-row-${rowIndex}` };
    headers.forEach((_, colIndex) => {
      row[`col_${colIndex}`] = cells[colIndex] ?? '';
    });
    return row;
  });
}
